<template>
  <div class="carbon-home-page">
    <filter-bar>
      <div class="filter-item">
        <span class="filter-label">核算标准</span>
        <el-select v-model="standard" size="small" style="width: 160px">
          <el-option
            v-for="item in standards"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
      </div>
      <div class="filter-item">
        <span class="filter-label">年份</span>
        <el-date-picker
          v-model="year"
          type="year"
          size="small"
          placeholder="选择年份"
          value-format="yyyy"
        />
      </div>
    </filter-bar>

    <div class="annual-section">
      <div class="annual-cards">
        <div class="annual-card carbon">
          <div class="card-label">年度碳排放总量</div>
          <div class="card-value">
            {{ formatNumber(annualStats.carbonEmission.value, 4) }}
            <span class="unit">{{ annualStats.carbonEmission.unit }}</span>
          </div>
          <div class="card-yoy">
            <i class="el-icon-bottom text-success"></i>
            较上年 {{ annualStats.carbonEmission.yoy }}%
          </div>
        </div>
        <div class="annual-card energy">
          <div class="card-label">年度能源消费总量</div>
          <div class="card-value">
            {{ formatNumber(annualStats.energyConsumption.value, 4) }}
            <span class="unit">{{ annualStats.energyConsumption.unit }}</span>
          </div>
          <div class="card-yoy">
            <i class="el-icon-bottom text-success"></i>
            较上年 {{ annualStats.energyConsumption.yoy }}%
          </div>
        </div>
      </div>
      <chart-card title="月度趋势" class="monthly-chart">
        <base-chart :option="monthlyChartOption" />
      </chart-card>
    </div>

    <div class="bottom-section">
      <chart-card title="碳流图" class="sankey-chart">
        <template slot="extra">
          <el-button type="text" size="small">展开全部</el-button>
        </template>
        <base-chart :option="sankeyOption" />
      </chart-card>
      <chart-card title="减排分析" class="reduction-chart">
        <base-chart :option="reductionOption" />
      </chart-card>
    </div>
  </div>
</template>

<script>
import FilterBar from '@/components/common/FilterBar.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getHomeData } from '@/api/modules/carbonAccounting'
import { formatNumber } from '@/utils/format'

export default {
  name: 'CarbonAccountingHome',
  components: { FilterBar, ChartCard, BaseChart },
  data() {
    return {
      standards: [],
      standard: 'GB/T 32150',
      year: '2025',
      annualStats: {
        carbonEmission: { value: 0, unit: 'tCO2e', yoy: 0 },
        energyConsumption: { value: 0, unit: 'tce', yoy: 0 }
      },
      monthlyChartOption: {},
      sankeyOption: {},
      reductionOption: {}
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getHomeData({ year: this.year, standard: this.standard })
      const data = res.data
      this.standards = data.standards
      this.standard = data.standards[0]
      this.year = String(data.year)
      this.annualStats = data.annualStats
      this.buildMonthlyChart(data.monthlyTrend)
      this.buildSankeyChart(data.carbonFlowSankey)
      this.buildReductionChart(data.reductionAnalysis)
    },
    buildMonthlyChart(trend) {
      this.monthlyChartOption = {
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['燃料燃烧排放', '净购入电力和热力排放', '过程排放', '碳排放总量'],
          bottom: 0
        },
        grid: { left: 50, right: 50, top: 30, bottom: 50 },
        xAxis: { type: 'category', data: trend.months },
        yAxis: [
          { type: 'value', name: 'tCO2e', position: 'left' },
          { type: 'value', name: 'tce', position: 'right' }
        ],
        series: [
          {
            name: '燃料燃烧排放',
            type: 'bar',
            stack: 'emission',
            data: trend.fuelCombustion,
            itemStyle: { color: '#1a3a5c' }
          },
          {
            name: '净购入电力和热力排放',
            type: 'bar',
            stack: 'emission',
            data: trend.purchasedEnergy,
            itemStyle: { color: '#409EFF' }
          },
          {
            name: '过程排放',
            type: 'line',
            yAxisIndex: 0,
            data: trend.processEmission,
            itemStyle: { color: '#E6A23C' },
            lineStyle: { color: '#E6A23C' }
          },
          {
            name: '碳排放总量',
            type: 'line',
            yAxisIndex: 0,
            data: trend.totalEmission,
            itemStyle: { color: '#F56C6C' },
            lineStyle: { color: '#F56C6C', width: 2 }
          }
        ]
      }
    },
    buildSankeyChart(sankey) {
      this.sankeyOption = {
        tooltip: { trigger: 'item' },
        series: [{
          type: 'sankey',
          layout: 'none',
          emphasis: { focus: 'adjacency' },
          data: sankey.nodes,
          links: sankey.links,
          lineStyle: { color: 'gradient', curveness: 0.5 },
          label: { fontSize: 11 }
        }]
      }
    },
    buildReductionChart(data) {
      this.reductionOption = {
        tooltip: { trigger: 'axis' },
        legend: { data: ['数量 (kWh)', '折合减排量 (tCO2e)'], bottom: 0 },
        grid: { left: 50, right: 50, top: 30, bottom: 50 },
        xAxis: { type: 'category', data: data.categories },
        yAxis: [
          { type: 'value', name: 'kWh' },
          { type: 'value', name: 'tCO2e' }
        ],
        series: [
          {
            name: '数量 (kWh)',
            type: 'bar',
            data: data.quantity,
            itemStyle: { color: '#1a3a5c' }
          },
          {
            name: '折合减排量 (tCO2e)',
            type: 'bar',
            yAxisIndex: 1,
            data: data.reduction,
            itemStyle: { color: '#409EFF' }
          }
        ]
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.annual-section {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  height: 320px;
}

.annual-cards {
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
}

.annual-card {
  flex: 1;
  background: @card-bg;
  border-radius: @card-radius;
  box-shadow: @card-shadow;
  padding: 20px;
  position: relative;
  overflow: hidden;

  &.carbon {
    background: linear-gradient(135deg, #fff 60%, rgba(64, 158, 255, 0.08));
  }

  &.energy {
    background: linear-gradient(135deg, #fff 60%, rgba(103, 194, 58, 0.08));
  }

  .card-label {
    font-size: 14px;
    color: @text-secondary;
    margin-bottom: 12px;
  }

  .card-value {
    font-size: 28px;
    font-weight: 700;
    color: @text-primary;

    .unit {
      font-size: 14px;
      font-weight: 400;
      color: @text-secondary;
      margin-left: 4px;
    }
  }

  .card-yoy {
    margin-top: 12px;
    font-size: 13px;
    color: @text-secondary;
  }
}

.monthly-chart {
  flex: 1;
}

.bottom-section {
  display: flex;
  gap: 16px;
  height: 360px;

  .sankey-chart,
  .reduction-chart {
    flex: 1;
  }
}
</style>
